import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useSalesAnalytics } from "@/hooks/useSalesAnalytics";
import { useState } from "react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { 
  TrendingUp, 
  BarChart3, 
  PieChart as PieChartIcon,
  LineChart as LineChartIcon,
  Users,
  Download
} from "lucide-react";

interface EmployeeComparisonChartProps {
  timeFilter: string;
  departmentFilter: string;
}

export const EmployeeComparisonChart = ({ timeFilter, departmentFilter }: EmployeeComparisonChartProps) => {
  const { data: salesData, isLoading } = useSalesAnalytics();
  const [chartType, setChartType] = useState("bar");
  const [metric, setMetric] = useState("revenue");

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-80 bg-gray-200 rounded"></div>
              </div>
            </CardContent>
          </Card>
        </div>
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const chartData = salesData?.topEmployees?.map((employee, index) => ({
    name: employee.name.split(' ')[0], // عرض الاسم الأول فقط
    fullName: employee.name,
    revenue: employee.total,
    transactions: employee.count,
    average: employee.average,
    department: employee.department,
    rank: index + 1
  })) || [];

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];

  const renderChart = () => {
    switch (chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="name" 
                className="text-sm"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-sm"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => 
                  metric === "revenue" ? `${(value/1000).toFixed(0)}ك` : value.toString()
                }
              />
              <Tooltip 
                formatter={(value: any, name: string) => [
                  metric === "revenue" ? `${value.toLocaleString('ar-SA')} ر.س` : value,
                  metric === "revenue" ? "الإيرادات" : metric === "transactions" ? "المعاملات" : "المتوسط"
                ]}
                labelFormatter={(label) => {
                  const employee = chartData.find(emp => emp.name === label);
                  return employee ? employee.fullName : label;
                }}
              />
              <Line 
                type="monotone" 
                dataKey={metric === "revenue" ? "revenue" : metric === "transactions" ? "transactions" : "average"}
                stroke="#3B82F6" 
                strokeWidth={3}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, fill: '#1D4ED8' }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case "pie":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey={metric === "revenue" ? "revenue" : "transactions"}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: any) => [
                  metric === "revenue" ? `${value.toLocaleString('ar-SA')} ر.س` : value,
                  metric === "revenue" ? "الإيرادات" : "المعاملات"
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="name" 
                className="text-sm"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-sm"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => 
                  metric === "revenue" ? `${(value/1000).toFixed(0)}ك` : value.toString()
                }
              />
              <Tooltip 
                formatter={(value: any, name: string) => [
                  metric === "revenue" ? `${value.toLocaleString('ar-SA')} ر.س` : value,
                  metric === "revenue" ? "الإيرادات" : metric === "transactions" ? "المعاملات" : "المتوسط"
                ]}
                labelFormatter={(label) => {
                  const employee = chartData.find(emp => emp.name === label);
                  return employee ? employee.fullName : label;
                }}
              />
              <Bar 
                dataKey={metric === "revenue" ? "revenue" : metric === "transactions" ? "transactions" : "average"}
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                مقارنة أداء الموظفين
              </CardTitle>
              
              <div className="flex items-center gap-2">
                <Select value={metric} onValueChange={setMetric}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">الإيرادات</SelectItem>
                    <SelectItem value="transactions">عدد المعاملات</SelectItem>
                    <SelectItem value="average">متوسط المعاملة</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="flex border rounded-lg p-1 bg-slate-50">
                  <Button
                    variant={chartType === "bar" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setChartType("bar")}
                    className="p-2"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={chartType === "line" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setChartType("line")}
                    className="p-2"
                  >
                    <LineChartIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={chartType === "pie" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setChartType("pie")}
                    className="p-2"
                  >
                    <PieChartIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              renderChart()
            ) : (
              <div className="h-80 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>لا توجد بيانات لعرضها</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <div className="space-y-6">
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5" />
              إحصائيات المقارنة
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {chartData.slice(0, 5).map((employee, index) => (
              <div key={employee.name} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: colors[index] }}></div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{employee.fullName}</p>
                    <p className="text-xs text-slate-500">{employee.department}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">
                    {metric === "revenue" 
                      ? `${employee.revenue.toLocaleString('ar-SA')} ر.س`
                      : metric === "transactions" 
                      ? `${employee.transactions} معاملة`
                      : `${employee.average.toLocaleString('ar-SA')} ر.س`
                    }
                  </p>
                  <Badge variant="outline" className="text-xs">
                    #{employee.rank}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Download className="w-5 h-5" />
              تصدير التقرير
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-100 text-sm mb-4">
              احصل على تقرير مفصل لأداء الموظفين
            </p>
            <Button variant="secondary" className="w-full">
              <Download className="w-4 h-4 ml-2" />
              تحميل التقرير
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};